import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { useAuth } from '@/hooks/useAuth';
import { useCampaignManagerSimple } from '@/hooks/useCampaignManagerSimple';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { campaignSchema, CampaignFormData, defaultFormValues } from '@/types/campaignForm';
import { Step1BasicInfo } from './steps/Step1BasicInfo';
import { Step2AIValidation } from './steps/Step2AIValidation';
import { Step3Customization } from './steps/Step3Customization';
import { Step4Confirmation } from './steps/Step4Confirmation';
import { AIAnalysisModal } from './AIAnalysisModal';
import { DraftIndicator } from './DraftIndicator';

interface CampaignWizardProps {
  onSuccess: () => void;
  onCancel: () => void;
  draftId?: string;
}

const steps = [
  { id: 1, title: 'Configuration', description: 'Informations de base' },
  { id: 2, title: 'Analyse IA', description: 'Validation des données' },
  { id: 3, title: 'Personnalisation', description: 'Critères et délais' },
  { id: 4, title: 'Confirmation', description: 'Récapitulatif et lancement' },
];

export const CampaignWizardNew = ({ onSuccess, onCancel, draftId }: CampaignWizardProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isAnalysisRunning, setIsAnalysisRunning] = useState(false);
  const [showAIAnalysisModal, setShowAIAnalysisModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [aiAnalysisData, setAiAnalysisData] = useState<any>(null);

  const form = useForm<CampaignFormData>({
    resolver: zodResolver(campaignSchema),
    defaultValues: defaultFormValues,
  });

  // Utiliser le hook de gestion de campagne
  const {
    campaignId,
    isDraft,
    isSubmitting,
    submissionStep,
    saveDraft,
    submitCampaign: hookSubmitCampaign,
    createDraftOnDemand,
    setCampaignId,
    setIsDraft,
  } = useCampaignManagerSimple({
    userId: user?.id,
    draftId,
    isDialog: false,
    onSuccess,
  });

  // Charger les données du brouillon si nécessaire
  useEffect(() => {
    const loadDraftData = async () => {
      if (draftId && user?.id) {
        console.warn('🔄 Chargement brouillon:', draftId);

        try {
          const { data: campaign, error } = await supabase
            .from('campaigns')
            .select('*')
            .eq('id', draftId)
            .eq('recruiter_id', user.id)
            .eq('is_draft', true)
            .single();

          if (error) {
            console.error('❌ Erreur chargement brouillon:', error);
            return;
          }

          if (campaign) {
            console.warn('✅ Brouillon chargé:', campaign);

            // Restaurer les données du formulaire
            form.setValue('candidate_name', campaign.candidate_name || '');
            form.setValue('candidate_email', campaign.candidate_email || '');
            form.setValue('candidate_phone', campaign.candidate_phone || '');
            form.setValue('target_position', campaign.target_position || '');
            form.setValue('candidate_cv_url', campaign.candidate_cv_url || '');
            form.setValue('job_description_file_url', campaign.job_description_file_url || '');
            form.setValue('job_description_text', campaign.job_description_text || '');
            form.setValue('selected_evaluation_criteria',
              Array.isArray(campaign.selected_evaluation_criteria)
                ? campaign.selected_evaluation_criteria as any[]
                : []
            );
            form.setValue('campaign_duration',
              (campaign.campaign_duration === '3_days' || campaign.campaign_duration === '5_days')
                ? campaign.campaign_duration
                : '3_days'
            );
            form.setValue('reminders_enabled', campaign.reminders_enabled ?? true);

            // Détection et correction automatique des campagnes bloquées
            let correctedStep = campaign.current_step || 1;
            if (campaign.ai_extracted_data && correctedStep === 1) {
              console.warn('🔧 Détection campagne bloquée - correction automatique vers étape 2');
              correctedStep = 2;

              // Corriger en base de données
              try {
                await supabase
                  .from('campaigns')
                  .update({ current_step: 2 })
                  .eq('id', campaign.id);
                console.warn('✅ Étape corrigée en base de données');
              } catch (updateError) {
                console.warn('⚠️ Erreur correction étape:', updateError);
              }
            }

            // Restaurer l'étape et les données IA
            setCurrentStep(correctedStep);
            if (campaign.ai_extracted_data) {
              setAiAnalysisData(campaign.ai_extracted_data);
            }

            // Mettre à jour les IDs
            setCampaignId(campaign.id);
            setIsDraft(true);
          }
        } catch (_error) {
          console.error('❌ Exception chargement brouillon:', _error);
        }
      }
    };

    loadDraftData();
  }, [draftId, user?.id, form, setCampaignId, setIsDraft]);

  // Objet draft temporaire pour compatibilité
  const draft = {
    currentStep,
    updateStep: setCurrentStep,
    campaignId,
    isDraft,
    aiAnalysisData,
    updateAIData: setAiAnalysisData,
    saveDraft: (data: any, step: number) => saveDraft(data, step, false, true), // isAutoSave = true par défaut
    saveWithAI: async (data: any, step: number, aiData: any) => {
      setAiAnalysisData(aiData);

      // Sauvegarder avec les données IA en base de données
      if (campaignId) {
        try {
          const { error } = await supabase
            .from('campaigns')
            .update({
              ai_extracted_data: aiData,
              current_step: step,
              updated_at: new Date().toISOString()
            })
            .eq('id', campaignId);

          if (error) {
            console.error('❌ Erreur sauvegarde données IA:', error);
          } else {
            console.warn('✅ Données IA sauvegardées en base');
          }
        } catch (_error) {
          console.error('❌ Exception sauvegarde données IA:', _error);
        }
      }

      return saveDraft(data, step, false, true); // isAutoSave = true pour éviter redirection
    },
    isDirty: false,
    isAutoSaving: false,
    lastSaved: null
  };

  const handleDataExtracted = async (data: any) => {
    console.warn('📋 Données extraites par IA:', data);
    draft.updateAIData(data);
    setIsAnalysisRunning(false);

    // Forcer le pré-remplissage des champs candidat avec les données IA
    if (data.candidateInfo?.name) {
      form.setValue('candidate_name', data.candidateInfo.name, { shouldValidate: true, shouldDirty: true });
    }
    if (data.candidateInfo?.email) {
      form.setValue('candidate_email', data.candidateInfo.email, { shouldValidate: true, shouldDirty: true });
    }
    if (data.targetPosition) {
      form.setValue('target_position', data.targetPosition, { shouldValidate: true, shouldDirty: true });
    }
    if (data.recommendedReferents?.length > 0) {
      form.setValue('recommended_referents', data.recommendedReferents, { shouldValidate: true, shouldDirty: true });
    }
    if (data.evaluationCriteria?.length > 0) {
      form.setValue('selected_evaluation_criteria', data.evaluationCriteria, { shouldValidate: true, shouldDirty: true });
    }

    // Sauvegarder immédiatement avec les données IA
    await draft.saveWithAI(form.getValues(), draft.currentStep, data);
  };

  const handleNext = async () => {
    if (draft.currentStep < steps.length) {
      // Si on passe de l'étape 1 à l'étape 2, déclencher l'analyse IA
      if (draft.currentStep === 1) {
        // S'assurer qu'un brouillon existe avant l'analyse IA
        if (!draft.campaignId) {
          await draft.saveDraft(form.getValues(), 1);
        }

        console.warn('🔍 CampaignWizard - Déclenchement analyse IA:', {
          campaignId: draft.campaignId,
          cvPath: form.watch('candidate_cv_url'),
          jobDescriptionPath: form.watch('job_description_file_url'),
          jobDescriptionText: form.watch('job_description_text')?.length || 0
        });
        setShowAIAnalysisModal(true);
        return;
      }

      // Validation spécifique pour l'étape 3 (Personnalisation)
      if (draft.currentStep === 3) {
        const isValid = await form.trigger(['campaign_duration', 'selected_evaluation_criteria']);
        if (!isValid) {
          toast({
            title: "Champs requis",
            description: "Veuillez remplir tous les champs obligatoires avant de continuer.",
            variant: "destructive",
          });
          return;
        }
      }

      const nextStep = draft.currentStep + 1;
      draft.updateStep(nextStep);

      // Navigation instantanée vers le haut de la page
      window.scrollTo(0, 0);

      // Auto-save lors du changement d'étape
      await draft.saveDraft(form.getValues(), nextStep);
    }
  };

  const handlePrevious = () => {
    if (draft.currentStep > 1) {
      const prevStep = draft.currentStep - 1;
      draft.updateStep(prevStep);
      
      // Navigation instantanée vers le haut de la page
      window.scrollTo(0, 0);
      
      // Auto-save lors du changement d'étape
      draft.saveDraft(form.getValues(), prevStep);
    }
  };

  const handleAIAnalysisComplete = async (data: any) => {
    console.warn('🔍 handleAIAnalysisComplete appelé avec:', { data, hasData: !!data });
    setShowAIAnalysisModal(false);

    if (data) {
      console.warn('✅ Données IA reçues, traitement et passage à l\'étape 2');
      await handleDataExtracted(data);

      // Mettre à jour l'étape en mémoire ET en base de données
      draft.updateStep(2);
      
      // Navigation instantanée vers le haut de la page
      window.scrollTo(0, 0);
      
      await saveDraft(form.getValues(), 2, false, true); // ✅ isAutoSave = true pour éviter onSuccess

      console.warn('✅ Navigation vers étape 2 terminée');
    } else {
      console.warn('❌ Analyse IA échouée, maintien à l\'étape 1');
      // Ne pas déclencher onSuccess ici, rester sur l'étape 1
      toast({
        title: "Analyse IA échouée",
        description: "L'analyse des documents a échoué. Veuillez vérifier vos fichiers et réessayer.",
        variant: "destructive",
      });
    }
  };

  const handleSaveDraft = useCallback(async () => {
    // Créer un brouillon à la demande si nécessaire
    if (!campaignId) {
      const newCampaignId = await createDraftOnDemand();
      if (!newCampaignId) return;
    }
    saveDraft(form.getValues(), currentStep, isAnalysisRunning, false);
  }, [campaignId, createDraftOnDemand, saveDraft, form, currentStep, isAnalysisRunning]);

  // Fonction de sauvegarde automatique (sans redirection)
  const handleAutoSaveDraft = useCallback(async () => {
    // Créer un brouillon à la demande si nécessaire
    if (!campaignId) {
      const newCampaignId = await createDraftOnDemand();
      if (!newCampaignId) return;
    }
    // isAutoSave = true pour éviter la redirection
    saveDraft(form.getValues(), currentStep, isAnalysisRunning, true);
  }, [campaignId, createDraftOnDemand, saveDraft, form, currentStep, isAnalysisRunning]);

  // Raccourci clavier Ctrl+S pour sauvegarder
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        console.warn('💾 Sauvegarde manuelle via Ctrl+S');
        handleSaveDraft();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSaveDraft]);

  // Utiliser la vraie fonction de soumission du hook
  const submitCampaign = hookSubmitCampaign;

  const renderCurrentStep = () => {
    switch (draft.currentStep) {
      case 1:
        return (
          <Step1BasicInfo
            control={form.control}
            form={form}
            onNext={handleNext}
            onCancel={onCancel}
            onDataExtracted={handleDataExtracted}
            campaignId={draft.campaignId}
            isAnalysisRunning={isAnalysisRunning}
            onAnalysisComplete={() => draft.updateStep(2)}
          />
        );
      case 2:
        return (
          <Step2AIValidation
            control={form.control}
            aiAnalysisData={draft.aiAnalysisData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onCancel={onCancel}
            onSaveDraft={handleSaveDraft}
            isSubmitting={isSubmitting}
          />
        );
      case 3:
        return (
          <Step3Customization
            control={form.control}
            aiAnalysisData={draft.aiAnalysisData}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onCancel={onCancel}
            onSaveDraft={handleSaveDraft}
            onAutoSaveDraft={handleAutoSaveDraft}
            isSubmitting={isSubmitting}
          />
        );
      case 4:
        return (
          <Step4Confirmation
            form={form}
            aiAnalysisData={draft.aiAnalysisData}
            onPrevious={handlePrevious}
            onCancel={onCancel}
            onSaveDraft={handleSaveDraft}
            onSubmit={submitCampaign}
            isSubmitting={isSubmitting}
            isDraft={draft.isDraft}
            submissionStep={submissionStep}
            campaignId={draft.campaignId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 relative">
      {/* Indicateur de brouillon */}
      <div className="flex justify-end">
        <DraftIndicator
          isDirty={draft.isDirty}
          isAutoSaving={draft.isAutoSaving}
          lastSaved={draft.lastSaved}
        />
      </div>

      {/* Header avec breadcrumb des étapes */}
      <div className="flex items-center justify-center py-8">
        <div className="grid grid-cols-7 items-center gap-0 w-full max-w-2xl">
          {/* Étape 1 */}
          <div
            className="flex flex-col items-center gap-3 col-span-1 cursor-pointer"
            onClick={() => {
              if (draft.currentStep > 1) {
                draft.updateStep(1);
                // Navigation instantanée vers le haut de la page
                window.scrollTo(0, 0);
                draft.saveDraft(form.getValues(), 1);
              }
            }}
          >
            <div className={`size-12 rounded-full flex items-center justify-center transition-colors ${draft.currentStep === 1 ? 'bg-teal-400' :
              draft.currentStep > 1 ? 'bg-teal-400' :
                'border border-neutral-400/20 hover:border-gray-400'
              }`}>
              <div className={`text-xl font-semibold font-bricolage ${draft.currentStep >= 1 ? 'text-white' : 'text-gray-800'
                }`}>1</div>
            </div>
            <div className={`text-sm font-medium font-bricolage leading-none text-center ${draft.currentStep === 1 ? 'text-gray-800' : 'text-neutral-400'
              }`}>Configuration</div>
          </div>

          {/* Ligne de connexion */}
          <div className={`h-0 border-t col-span-1 ${draft.currentStep > 1 ? 'border-teal-400' : 'border-neutral-400/20'
            }`}></div>

          {/* Étape 2 */}
          <div
            className="flex flex-col items-center gap-3 col-span-1 cursor-pointer"
            onClick={() => {
              if (draft.currentStep > 2 && draft.aiAnalysisData) {
                draft.updateStep(2);
                // Navigation instantanée vers le haut de la page
                window.scrollTo(0, 0);
                draft.saveDraft(form.getValues(), 2);
              }
            }}
          >
            <div className={`size-12 rounded-full flex items-center justify-center transition-colors ${draft.currentStep === 2 ? 'bg-teal-400' :
              draft.currentStep > 2 ? 'bg-teal-400' :
                'border border-neutral-400/20 hover:border-gray-400'
              }`}>
              <div className={`text-xl font-semibold font-bricolage ${draft.currentStep >= 2 ? 'text-white' : 'text-gray-800'
                }`}>2</div>
            </div>
            <div className={`text-sm font-medium font-bricolage leading-none text-center ${draft.currentStep === 2 ? 'text-gray-800' : 'text-neutral-400'
              }`}>Analyse de l'IA</div>
          </div>

          {/* Ligne de connexion */}
          <div className={`h-0 border-t col-span-1 ${draft.currentStep > 2 ? 'border-teal-400' : 'border-neutral-400/20'
            }`}></div>

          {/* Étape 3 */}
          <div
            className="flex flex-col items-center gap-3 col-span-1 cursor-pointer"
            onClick={() => {
              if (draft.currentStep > 3) {
                draft.updateStep(3);
                // Navigation instantanée vers le haut de la page
                window.scrollTo(0, 0);
                draft.saveDraft(form.getValues(), 3);
              }
            }}
          >
            <div className={`size-12 rounded-full flex items-center justify-center transition-colors ${draft.currentStep === 3 ? 'bg-teal-400' :
              draft.currentStep > 3 ? 'bg-teal-400' :
                'border border-neutral-400/20 hover:border-gray-400'
              }`}>
              <div className={`text-xl font-semibold font-bricolage ${draft.currentStep >= 3 ? 'text-white' : 'text-gray-800'
                }`}>3</div>
            </div>
            <div className={`text-sm font-medium font-bricolage leading-none text-center ${draft.currentStep === 3 ? 'text-gray-800' : 'text-neutral-400'
              }`}>Personnalisation</div>
          </div>

          {/* Ligne de connexion */}
          <div className={`h-0 border-t col-span-1 ${draft.currentStep > 3 ? 'border-teal-400' : 'border-neutral-400/20'
            }`}></div>

          {/* Étape 4 */}
          <div
            className="flex flex-col items-center gap-3 col-span-1 cursor-pointer"
            onClick={() => {
              if (draft.currentStep > 4) {
                draft.updateStep(4);
                // Navigation instantanée vers le haut de la page
                window.scrollTo(0, 0);
              }
            }}
          >
            <div className={`size-12 rounded-full flex items-center justify-center transition-colors ${draft.currentStep === 4 ? 'bg-gray-800' :
              draft.currentStep > 4 ? 'bg-teal-400' :
                'border border-neutral-400/20 hover:border-gray-400'
              }`}>
              <div className={`text-xl font-semibold font-bricolage ${draft.currentStep >= 4 ? 'text-white' : 'text-gray-800'
                }`}>4</div>
            </div>
            <div className={`text-sm font-medium font-bricolage leading-none text-center ${draft.currentStep === 4 ? 'text-gray-800' : 'text-neutral-400'
              }`}>Confirmation</div>
          </div>
        </div>
      </div>

      {/* Form container */}
      <Form {...form}>
        <div className="min-h-[600px]">
          {renderCurrentStep()}
        </div>
      </Form>

      {/* Modal d'analyse IA */}
      <AIAnalysisModal
        isOpen={showAIAnalysisModal}
        onComplete={handleAIAnalysisComplete}
        campaignId={draft.campaignId}
        cvPath={form.watch('candidate_cv_url')}
        jobDescriptionPath={form.watch('job_description_file_url')}
        jobDescriptionText={form.watch('job_description_text')}
      />
    </div>
  );
};